import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ApiClient from '../client';
import { ENDPOINTS } from '../endpoints';
import { CreateProjectDto } from '../types';
import { queryKeys } from '../keys';

const client = new ApiClient(process.env.API_URL!, 'app');

// Queries
export const useCompanyProject = (companyId: string) =>
  useQuery({
    queryKey: queryKeys.companyProject(companyId),
    queryFn: () => client.get(ENDPOINTS.COMPANY_PROJECT(companyId)),
    enabled: !!companyId,
  });

export const useProject = (projectId: string) =>
  useQuery({
    queryKey: queryKeys.project(projectId),
    queryFn: () => client.get(ENDPOINTS.PROJECT(projectId)),
    enabled: !!projectId,
  });

export const useProjectFields = (projectId: string, groups?: string[]) =>
  useQuery({
    queryKey: queryKeys.projectFields(projectId),
    queryFn: () =>
      client.get(ENDPOINTS.PROJECT_FIELDS(projectId), {
        params: groups ? { groups: groups.join(',') } : undefined,
      }),
    enabled: !!projectId,
  });

export const useProjectCarrierMappings = (projectId: string) =>
  useQuery({
    queryKey: queryKeys.projectCarrierMappings(projectId),
    queryFn: () => client.get(ENDPOINTS.PROJECT_CARRIER_MAPPINGS(projectId)),
    enabled: !!projectId,
  });

// Mutations
export const useCreateProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateProjectDto) => client.post(ENDPOINTS.PROJECTS, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['companyProjects'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['companyProject'], refetchType: 'all' });
    },
  });
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => client.delete(ENDPOINTS.PROJECT(projectId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['companyProjects'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['companyProject'], refetchType: 'all' });
    },
  });
};
